import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import BottomTabBar from "@/components/layout/BottomTabBar";

// Lazy-loaded routes for code splitting
const Index = lazy(() => import("./pages/Index"));
const Listings = lazy(() => import("./pages/Listings"));
const ListingDetail = lazy(() => import("./pages/ListingDetail"));
const Auth = lazy(() => import("./pages/Auth"));
const MyPerch = lazy(() => import("./pages/MyPerch"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ListProperty = lazy(() => import("./pages/ListProperty"));
const Settings = lazy(() => import("./pages/Settings"));
const Pricing = lazy(() => import("./pages/Pricing"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerificationCallback = lazy(() => import("./pages/VerificationCallback"));
const CityCommunitiesPage = lazy(() => import("./pages/CityCommunitiesPage"));
const CommunityDetailPage = lazy(() => import("./pages/CommunityDetailPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ListPropertyWithoutRealtor = lazy(() => import("./pages/ListPropertyWithoutRealtor"));
const SellWithoutRealtorOntario = lazy(() => import("./pages/SellWithoutRealtorOntario"));

const queryClient = new QueryClient();

// Minimal loading fallback that doesn't affect layout
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <Toaster />
            <Sonner />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/listings" element={<Listings />} />
                <Route path="/listings/:id" element={<ListingDetail />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/my-perch" element={<MyPerch />} />
                <Route path="/dashboard" element={<MyPerch />} />
                <Route path="/seller-dashboard" element={<MyPerch />} />
                <Route path="/admin" element={<AdminDashboard />} />
                <Route path="/list-property" element={<ListProperty />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/verification-callback" element={<VerificationCallback />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/list-property-without-realtor" element={<ListPropertyWithoutRealtor />} />
                <Route path="/sell-without-realtor-ontario" element={<SellWithoutRealtorOntario />} />
                {/* City community index routes */}
                <Route path="/:city/communities" element={<CityCommunitiesPage />} />
                {/* Community detail route */}
                <Route path="/:city/:slug" element={<CommunityDetailPage />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            {/* Bottom Tab Bar for mobile navigation */}
            <BottomTabBar />
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
